import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { DownloadPdfIcon } from '@/components/Icons';
import LoadingComponent from '@/components/Loading';
import { useProofOfAccount } from '@/features/selectedAccount/hooks/useProofOfAccount';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

interface ProofOfAccountProps {
  walletId: string;
}

const ProofOfAccount: React.FC<ProofOfAccountProps> = ({ walletId }) => {
  const { data, isLoading, isError } = useProofOfAccount(walletId);
  const proofData = data?.data;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (!proofData?.proofOfAccount) return;
    setPdfLoading(true);

    fetch(proofData.proofOfAccount)
      .then((res) => res.arrayBuffer())
      .then((arrayBuffer) => {
        return pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      })
      .then((pdf) => {
        return pdf.getPage(1);
      })
      .then((page) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const scale = 2;
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderContext = {
          canvasContext: canvas.getContext('2d')!,
          viewport: viewport,
          canvas: canvas
        };

        return page.render(renderContext).promise;
      })
      .catch((err) => {
        console.error('Failed to render PDF:', err);
      })
      .finally(() => setPdfLoading(false));
  }, [proofData?.proofOfAccount]);

  const handleDownload = () => {
    if (!proofData?.proofOfAccount) return;
    window.open(proofData.proofOfAccount, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div className="bg-white flex justify-center items-center min-h-[200px]">
        <LoadingComponent />
      </div>
    );
  }

  if (isError || !proofData) {
    return (
      <div className="bg-white flex justify-center items-center min-h-[200px]">
        <span className="text-sm text-aux-gray">No proof of account available.</span>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="relative w-[688px] bg-emerge-base-3 rounded-xl border border-aux-gray overflow-auto">
        {pdfLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-20">
            <LoadingComponent />
          </div>
        )}
        <canvas ref={canvasRef} className="w-full" />

        <button
          type="button"
          onClick={handleDownload}
          className="absolute top-4 right-4 flex items-center gap-2 px-4 py-2 bg-emerge-teal rounded-full text-xs font-medium text-emerge-navy z-10 hover:bg-emerge-teal/90"
          aria-label="Download proof of account PDF">
          <DownloadPdfIcon size="xs" />
          Download pdf
        </button>
      </div>
    </div>
  );
};

export default ProofOfAccount;
